import { useState } from "react";

/**
 * Base-Template: geführte Plausibilitäts-Navigation (2026-06-10, Thomas).
 *
 * Übernimmt die Stundenlisten-Mechanik (focusCell: Selector über
 * data-Attribute + focus/select/scrollIntoView, ‹ ›-Navigation mit
 * Wrap-around) als WIEDERVERWENDBARE Leiste — same code, 0 drift:
 * jede Detailseite mit data-row/data-field-Zellen kann sie laden.
 *
 * Kern-UX-Problem das sie löst: ein rotes Feld ohne sichtbares WARUM
 * ("ich weiß nicht was ich machen soll") — die Leiste zeigt die Begründung
 * des aktuellen Fehlers IM KLARTEXT und springt geführt von Fehler zu
 * Fehler. Funktioniert in Übersicht UND Vergleichen: der Selector nimmt
 * das SICHTBARE Element (display:none-View wird übersprungen).
 */
export interface PlausibilityNavProps {
  /** Map "rowIndex.field" → Begründung (nur echte/rote Befunde). */
  issues: Record<string, string>;
  /** Zusätzlich aktive Zelle in den Seiten-State spiegeln (Hover-Sync). */
  onFocusCell?: (row: number, field: string) => void;
}

function focusVisibleCell(row: number, field: string) {
  requestAnimationFrame(() => {
    const els = document.querySelectorAll<HTMLInputElement>(
      `[data-row="${row}"][data-field="${field}"] input`,
    );
    // Beide Views tragen dieselben data-Attribute — die unsichtbare
    // (display:none) hat offsetParent === null und wird übersprungen.
    const visible = [...els].find((el) => el.offsetParent !== null) ?? els[0];
    visible?.focus();
    visible?.select?.();
    visible?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
  });
}

export function PlausibilityNav({ issues, onFocusCell }: PlausibilityNavProps) {
  const keys = Object.keys(issues).sort((a, b) => {
    const [ra, fa] = a.split(".");
    const [rb, fb] = b.split(".");
    return (Number(ra) - Number(rb)) || String(fa).localeCompare(String(fb));
  });
  const [pos, setPos] = useState(0);
  if (keys.length === 0) return null;
  const cur = Math.min(pos, keys.length - 1);
  const curKey = keys[cur]!;
  const [rowStr, field] = curKey.split(".");
  const row = Number(rowStr);

  const go = (idx: number) => {
    const wrapped = (idx + keys.length) % keys.length;
    setPos(wrapped);
    const [r, f] = keys[wrapped]!.split(".");
    focusVisibleCell(Number(r), String(f));
    onFocusCell?.(Number(r), String(f));
  };

  return (
    <div style={{
      background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #dc2626",
      borderRadius: 4, padding: "8px 12px", marginBottom: 8, fontSize: 13, color: "#7f1d1d",
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    }}>
      <span className="ts-plaus-badge ts-plaus-badge-red" aria-hidden="true">!</span>
      <strong>{keys.length} Plausibilitäts-Fehler</strong>
      <span style={{ flex: "1 1 280px", minWidth: 0 }}>
        Zeile {row + 1}, {field}: {issues[curKey]}
      </span>
      <span style={{ display: "inline-flex", gap: 6 }}>
        <button type="button" className="link" onClick={() => go(cur)} title="Zum aktuellen Fehler springen (fokussiert das Feld)">
          → hinspringen
        </button>
        <button type="button" className="link" onClick={() => go(cur - 1)} title="Voriger Fehler" disabled={keys.length < 2}>
          ‹
        </button>
        <button type="button" className="link" onClick={() => go(cur + 1)} title="Nächster Fehler" disabled={keys.length < 2}>
          ›
        </button>
      </span>
    </div>
  );
}

/** Zell-Badge mit Hover-Tooltip — EXAKT die Stundenlisten-Klassen
 *  (ts-plaus-badge/-red + ts-tooltip) → identische Optik, 0 Drift. */
export function PlausibilityBadge({ reason }: { reason: string }) {
  return (
    <span className="ts-plaus-badge ts-plaus-badge-red ts-tooltip-trigger" aria-label={reason}>
      !
      <span className="ts-tooltip" role="tooltip">{reason}</span>
    </span>
  );
}
